import {
  type BriefFramingChange,
  type FollowUpFailureReason,
  type FollowUpOutcome,
  type FollowUpOutcomeKind,
  type BriefWordingRule,
  MAX_BRIEF_HEADLINE_CHARS,
  type NotSettledKind,
  QUALITY_FLOOR,
} from '@sigora/domain';

/**
 * What the Brief step, and the Fielding and Analyzing steps' lines about the Brief, say in Sigora's
 * own words: one module, so the product copy is one file to change. Everything else on those
 * screens is the stored Brief itself.
 */

export const OPEN_BRIEF_LABEL = 'Open the brief';

/** The block headings, in the Brief's order. The Verdict has none. */
export const BRIEF_HEADINGS = {
  summary: 'Executive summary',
  recommendation: 'Recommendation',
  notSettled: 'Not settled',
  evidence: 'Evidence by question',
  method: 'Method and audience',
  appendix: 'Appendix',
} as const;

/** Before the decision the chosen Goal informs. */
export const INFORMS_PREFIX = 'Informs: ';

/**
 * What a Not settled entry or card says when the synthesis did not word it, by its kind. An
 * `unsettled` entry always carries its own words.
 */
export const NOT_SETTLED_COPY: Record<Exclude<NotSettledKind, 'unsettled'>, string> = {
  not_asked: "This study didn't ask a question that could answer this.",
  no_answers: 'Nobody answered the questions asked about this.',
};

/** Under each option of a poll. */
export const pollLine = (count: number, respondents: number): string =>
  `${String(count)} of ${String(respondents)} people chose this.`;

/** Under each theme of a theme strip. */
export const themeLine = (participants: number, coded: number): string =>
  `${String(participants)} of ${String(coded)} people`;

/**
 * Under every theme strip: how people are counted and how many were coded, then, when the floor
 * left any theme out, how many and the floor. `leftOut` is absent when nothing was left out or the
 * stored visual does not say.
 */
export const themeFootnote = (
  coded: number,
  leftOut?: { count: number; floor: number },
): string => {
  const people = coded === 1 ? '1 person' : `${String(coded)} people`;
  const counted = `Each person is counted once in a theme. Coded from ${people}.`;
  if (leftOut === undefined) {
    return counted;
  }
  const fewer = `raised by fewer than ${String(leftOut.floor)} people`;

  return leftOut.count === 1
    ? `${counted} 1 smaller theme, ${fewer}, is left out.`
    : `${counted} ${String(leftOut.count)} smaller themes, ${fewer}, are left out.`;
};

/** The theme strip's toggle while `hidden` themes are behind it. */
export const themesMoreLabel = (hidden: number): string => `+${String(hidden)} more`;

/**
 * What the toggle is called to a screen reader while `hidden` themes are behind it: its visible
 * label, then what it counts, so a spoken command matching the label reaches it.
 */
export const themesMoreName = (hidden: number): string =>
  `${themesMoreLabel(hidden)} ${hidden === 1 ? 'theme' : 'themes'}`;

/** The theme strip's toggle while every theme shows. */
export const THEMES_FEWER_LABEL = 'Show fewer';

/** What the toggle is called to a screen reader while every theme shows. */
export const THEMES_FEWER_NAME = 'Show fewer themes';

export const READ_MORE_LABEL = 'Read more';
export const READ_LESS_LABEL = 'Read less';
export const ASK_ABOUT_THIS_LABEL = 'Ask about this';

export const completesLine = (completes: number, ordered: number): string =>
  `${String(completes)} people finished (${String(ordered)} ordered)`;

export const formatLine = (open: number, polls: number, minutes: number): string =>
  `Online conversation: ${String(open)} open questions and ${String(polls)} polls, about ${String(minutes)} minutes`;

export const fieldedLine = (launched: string, ended: string): string =>
  `Fielded ${launched}–${ended}`;

export const versionsLine = (versions: { rqs: number; dg: number; audience: number }): string =>
  `Versions: questions ${String(versions.rqs)}, guide ${String(versions.dg)}, audience ${String(versions.audience)}`;

/** The Limitation labels, as the Audience step gives them. */
export const LIMITATION_HEADINGS = {
  targetingGaps: "Can't be targeted",
  pipelineConstraints: 'How this study runs',
} as const;

export const SIMULATED_LINE = 'The people in this study were simulated, not recruited.';

/** The Appendix's own parts. */
export const APPENDIX_HEADINGS = {
  guide: 'The guide',
  polls: 'Poll counts',
  answers: 'Every answer',
  otherAnswers: 'Other answers',
} as const;

/*
 * What the Brief's follow-ups leave on it: a dated note under a card, and the Appendix's log of
 * every follow-up question with how it landed.
 */

/** The Appendix toggle on the Brief as it stands now, naming how many follow-ups it logs. */
export const appendixToggleLabel = (followUps: number): string =>
  `Appendix (follow-up log, ${String(followUps)})`;

/** A note under its card: when it was added, then the answer's words. */
export const cardNoteLine = (date: string, text: string): string => `Note, ${date}: ${text}`;

export const followUpLogHeading = (followUps: number): string =>
  `Follow-up log (${String(followUps)})`;

export const FOLLOW_UP_LOG_EMPTY = 'No follow-up questions yet.';

/**
 * How a follow-up landed, as its log line ends. A note names the card it sits under by its research
 * question (`card`, or `null` when the Brief on screen has no such card).
 */
export const followUpOutcomeLine = (outcome: FollowUpOutcome, card: string | null): string => {
  switch (outcome.kind) {
    case 'update_brief':
      return outcome.changed
        ? `Updated the brief (v${String(outcome.briefVersion)}).`
        : `Checked against the brief (v${String(outcome.briefVersion)}); nothing needed to change.`;
    case 'note':
      return card === null ? 'Added as a note.' : `Added as a note to "${card}".`;
    case 'log':
      return 'Logged only.';
  }
};

/** One line of the follow-up log: when, the question as asked, and how it landed. */
export const followUpLogLine = (date: string, question: string, outcome: string): string =>
  `${date}: "${question}". ${outcome}`;

/*
 * The Brief's thread, where every question asked is a follow-up: the answer, the choice of how it
 * lands, and what each way it can stand says.
 */

/** Under a question while Sigora answers it, and beside the closed Ask Sigora tab. */
export const FOLLOW_UP_ANSWERING = 'Sigora is answering…';

/** Under an answer while the update it was chosen for is written into the Brief. */
export const FOLLOW_UP_UPDATING = 'Sigora is updating the brief…';

/** Beside the closed Ask Sigora tab while an answer waits for its outcome. */
export const FOLLOW_UP_WAITING = 'A follow-up is waiting for your choice.';

/** Above the three outcomes, naming the card the answer relates to when it names one. */
export const followUpChoicePrompt = (card: string | null): string =>
  card === null ? 'How should this land?' : `This relates to "${card}". How should it land?`;

/** The three outcomes, in the order they are offered. */
export const FOLLOW_UP_CHOICE_LABELS: Record<FollowUpOutcomeKind, string> = {
  update_brief: 'Update the brief',
  note: 'Add as note',
  log: 'Log only',
};

/** Read after the outcome Sigora suggests, by a screen reader only; the styling says it to the eye. */
export const FOLLOW_UP_SUGGESTED_SUFFIX = ' (suggested)';

/** In place of the outcomes once the follow-up has one. */
export const followUpFinalizedLine = (outcome: FollowUpOutcome, card: string | null): string => {
  switch (outcome.kind) {
    case 'update_brief':
      return outcome.changed
        ? `Updated. The brief is now version ${String(outcome.briefVersion)}.`
        : 'Checked against the brief; nothing needed to change.';
    case 'note':
      return card === null
        ? 'Added as a note. Your stakeholder will see it.'
        : `Added as a note under "${card}". Your stakeholder will see it.`;
    case 'log':
      return "Logged. It's in the follow-up log in the appendix.";
  }
};

/**
 * Why a question has no answer, by its reason. The question is kept whatever the reason, and Try
 * again is always offered; no reason asks for anything but time.
 */
export const followUpFailureLine = (reason: FollowUpFailureReason): string => {
  switch (reason) {
    case 'budget_spent':
      return "Sigora has reached today's limit for follow-up answers. Your question is saved; try again later.";
    case 'timed_out':
      return 'Sigora took too long to answer. Your question is saved.';
    case 'model_unavailable':
      return "Sigora can't answer follow-up questions right now. Your question is saved.";
    case 'over_turn_cap':
      return 'This question needs a longer answer than Sigora gives at once. Your question is saved; asking about one card may help.';
    case 'evidence_unavailable':
      return "Sigora couldn't read this study's answers just now. Your question is saved.";
    case 'interrupted':
      return 'Answering was interrupted. Your question is saved.';
    case 'unreadable':
      return "Sigora's answer didn't hold to the brief's numbers and quotes, so it isn't shown. Your question is saved.";
  }
};

/** Above the outcomes when an update of the Brief could not be written. */
export const followUpUpdateFailedLine = (reason: FollowUpFailureReason): string =>
  reason === 'budget_spent'
    ? "Sigora has reached today's limit for follow-ups, so the brief wasn't updated. Try again later, or keep the answer as a note or a log line."
    : "The brief couldn't be updated. Try again, or keep the answer as a note or a log line.";

export const FOLLOW_UP_RETRY_LABEL = 'Try again';

/** An update whose job was lost before it wrote the brief; Try again asks for it anew. */
export const FOLLOW_UP_UPDATE_LOST =
  "The brief wasn't updated: the update stopped before it finished.";

/** Under a question, or an update, still pending well past the time it usually takes. */
export const FOLLOW_UP_STILL_WORKING = 'This is taking longer than usual.';
export const FOLLOW_UP_CHECK_AGAIN_LABEL = 'Check again';

/** A question the server never answered about. */
export const FOLLOW_UP_SEND_FAILED = "Your question wasn't sent. Send it again.";

/** Over the Brief's composer while the next question is about one card, from Ask about this. */
export const followUpScopeLine = (card: string): string => `About: ${card}`;
export const FOLLOW_UP_SCOPE_REMOVE_LABEL = 'Ask about the whole study';

/** A question about a card the brief no longer has, which was not sent. */
export const FOLLOW_UP_CARD_GONE =
  "That card isn't on the brief any more, so your question wasn't sent. Send it again to ask about the whole study.";

/** Under a question that was not taken, kept on screen until it is sent again or taken back. */
export const FOLLOW_UP_NOT_SENT = 'Not sent.';
export const FOLLOW_UP_SEND_AGAIN_LABEL = 'Send again';

/** A question asked while another is still being answered. */
export const FOLLOW_UP_ANSWER_RUNNING =
  'Sigora is still answering your last question. Ask this one once it has answered.';

/** Under a failed answer while another question is being answered, which Try again waits for. */
export const FOLLOW_UP_RETRY_WAITS =
  'Sigora is answering another question. Try this one again once it has answered.';

/** A failed answer whose reason this build does not know. */
export const FOLLOW_UP_FAILED = "Sigora couldn't answer this. Your question is saved.";

/** A question the server refused for any other reason. */
export const FOLLOW_UP_SEND_REFUSED = "Sigora couldn't take that question. Try again.";

/** An outcome, or a Try again, that could not be sent. */
export const FOLLOW_UP_ACTION_FAILED = "That didn't go through. Try again.";

/** The thread's earlier follow-ups could not be read. */
export const FOLLOW_UP_LOAD_FAILED =
  'The earlier follow-up questions could not be loaded. You can still ask one.';

/** Asking which card a note goes under, when neither the question nor its answer names one. */
export const NOTE_CARD_PICKER_LEGEND = 'Which card should the note go under?';
export const NOTE_CARD_PICKER_SUBMIT = 'Add the note';
export const NOTE_CARD_PICKER_CANCEL = 'Cancel';
export const NOTE_CARD_PICKER_UNAVAILABLE =
  "The brief's cards couldn't be loaded, so the note can't be placed yet. Try again.";

/** What the Analyzing step says about the Brief while the study is being analysed. */
export const SYNTHESIS_PENDING = 'Sigora is writing your brief.';
export const SYNTHESIS_FAILED = "Sigora couldn't write your brief.";
export const TRY_AGAIN_LABEL = 'Try again';
export const SYNTHESIS_DECLINED = `Fewer than ${String(QUALITY_FLOOR)} people finished, so Sigora won't write a brief.`;
export const SYNTHESIS_RETRY_FAILED = "The brief couldn't be asked for again just now.";

/** What the Fielding step says once the Brief is published. */
export const STUDY_COMPLETE_LINE = 'This study is finished, and its brief is ready.';

/** The Brief's own read. */
export const BRIEF_LOADING = 'Opening the brief…';
export const BRIEF_READ_FAILED = "The brief couldn't be opened.";
export const BRIEF_NOT_PUBLISHED = 'This study has no brief yet.';

/*
 * The researcher's framing on the Brief step: rewording the Verdict, hiding a summary bullet, moving
 * a recommendation, the marks those leave, and what an edit that was refused says.
 */

export const EDIT_WORDING_LABEL = 'Edit wording';
export const EDIT_WORDING_HINT = 'Wording is yours; the evidence and the confidence label are not.';
export const SAVE_LABEL = 'Save';
export const SAVING_LABEL = 'Saving…';
export const CANCEL_LABEL = 'Cancel';

/** The name of one run of words in the Verdict editor, counted around the Verdict's counts. */
export const wordingSlotLabel = (slot: number, slots: number): string =>
  `Wording, part ${String(slot)} of ${String(slots)}`;

/** The marks a block carries once the researcher has framed it. */
export const EDITED_BY_YOU = 'Edited by you';
export const REORDERED_BY_YOU = 'Reordered by you';

export const HIDE_LABEL = 'Hide';
export const SHOW_LABEL = 'Show';
export const MOVE_UP_LABEL = 'Move up';

/** What a screen reader hears before a hidden summary bullet's words. */
export const HIDDEN_BULLET_PREFIX = 'Hidden: ';

/** Why the Verdict's wording was refused, by the rule it broke. */
export const WORDING_REFUSED_COPY: Record<BriefWordingRule, string> = {
  gate: 'Keep numbers and quotations out of your wording. The counts come from the evidence.',
  too_long: `Keep the verdict to one line (${String(MAX_BRIEF_HEADLINE_CHARS)} characters).`,
  empty: 'The verdict needs some words.',
};

/** Hiding the only bullet still showing. */
export const SUMMARY_EMPTY_COPY = 'Keep at least one bullet showing.';

/** An edit made over a Brief that changed under it; the Brief on screen is read again. */
export const BRIEF_MOVED_NOTICE = 'This brief changed while you were editing it. Look again.';

/** An edit that failed for any other reason. */
export const BRIEF_EDIT_FAILED = "That change couldn't be saved. Try again.";

/*
 * The Brief's versions on the Brief step: the version line and its history, a past version opened
 * from it, and what Sigora's newest revision says on the blocks it changed.
 */

/** The version on screen, and what wrote it. */
export const briefVersionLine = (version: number, reasonText: string): string =>
  `Version ${String(version)} · ${reasonText}`;

export const historyToggleLabel = (versions: number): string => `History (${String(versions)})`;
export const HIDE_HISTORY_LABEL = 'Hide history';

/** One version in the history, newest first. */
export const briefHistoryLine = (version: number, reasonText: string): string =>
  `Version ${String(version)}: ${reasonText}.`;

export const ORIGINAL_TAG = 'Original';
export const CURRENT_TAG = 'Current';
export const VIEW_VERSION_LABEL = 'View';

/** Above a past version opened from the history. */
export const briefVersionBanner = (version: number, date: string): string =>
  `You're looking at version ${String(version)}, from ${date}.`;
export const BACK_TO_CURRENT_LABEL = 'Back to the current brief';

/** On each block Sigora's newest revision changed. */
export const updatedTag = (version: number): string => `Updated v${String(version)}`;

const recommendations = (count: number): string =>
  `${String(count)} ${count === 1 ? 'recommendation' : 'recommendations'}`;

/** What a revision did to the researcher's framing, one line per change, by its kind. */
export const briefFramingChangeLine = (change: BriefFramingChange): string => {
  switch (change.kind) {
    case 'verdict_wording_replaced':
      return "Sigora rewrote the verdict, so your wording was replaced. It's in the history.";
    case 'hidden_bullets_removed':
      return change.previous_ids.length === 1
        ? 'A bullet you hid is no longer in the brief.'
        : `${String(change.previous_ids.length)} bullets you hid are no longer in the brief.`;
    case 'hidden_bullets_shown':
      return 'Every bullet is showing again, because the ones left were all hidden.';
    case 'recommendations_changed_under_your_order': {
      const { added, removed } = change;
      if (added.length === 0) {
        return `Your order is kept; Sigora removed ${recommendations(removed)}.`;
      }
      const addedLine = `Your order is kept; Sigora added ${recommendations(added.length)} at the end`;
      return removed === 0 ? `${addedLine}.` : `${addedLine} and removed ${String(removed)}.`;
    }
  }
};
